import clsx from 'clsx';
import * as React from 'react';

import { conflicts } from '@gitbook/doc-core';
import * as hiveCore from '@gitbook/hive-core';
import { useHiveBranch, useHiveRevision } from '@gitbook/hive-react';
import { IntlPluralize, ModalAnimateLifecycle } from '@gitbook/react-uikit';
import type { Block, Document } from '@gitbook/slate';
import { findDOMNode } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';
import { Badge, Button, Card, LoadingIndicatorIcon, toast } from '@gitbook/spine-react';
import type { List } from '@gitbook/util-immutable';
import { useLoadingState } from '@gitbook/util-react-hooks';
import { scrollToVirtualNode } from '@gitbook/util-react-virtual';
import { assertIsDefined, assertNever } from '@gitbook/util-ts';

import { ConflictsKeepPageModal } from './ConflictsKeepPageModal';
import { DeletePageDialog } from './DeletePageDialog';

interface ConflictsPageBannerProps {
    page: hiveCore.RevisionPageDocument;
    conflictType: hiveCore.RevisionConflictState | false;
    document: Document;
}

/**
 * Banner displayed at the top of a page to indicate that the page has unresolved conflicts.
 */
export function ConflictsPageBanner(props: ConflictsPageBannerProps) {
    const { page, document, conflictType } = props;

    const hiveBranch = useHiveBranch();
    const [loading, wrapLoading] = useLoadingState();

    // If the page has been deleted `canBeResolved` always be true
    const conflictNodes = conflicts.getDocumentConflicts(document);
    const canBeResolved = conflictNodes.isEmpty();

    const onPressMarkAsResolved = () => {
        assertIsDefined(hiveBranch);

        if (!canBeResolved) {
            return;
        }
        wrapLoading(async () => {
            await hiveBranch.change((change) => {
                change.resolveConflict(page.id);
            });

            if (!hiveBranch.revision.conflicts) {
                toast.success('All conflicts have been resolved', {
                    description: 'You can now merge this change request.',
                });
            }
        });
    };

    switch (conflictType) {
        case hiveCore.RevisionConflictState.DeletedInHead:
        case hiveCore.RevisionConflictState.DeletedInBase:
        case hiveCore.RevisionConflictState.DeletedInBaseSubtreeModifiedInHead:
        case hiveCore.RevisionConflictState.DeletedInHeadSubtreeModifiedInBase:
            return (
                <DeleteInBaseOrInHeadConflict
                    {...props}
                    conflictType={conflictType}
                    onPressMarkAsResolved={onPressMarkAsResolved}
                    loading={loading}
                />
            );

        case hiveCore.RevisionConflictState.DocumentConflict:
            return (
                <PageDocumentConflict
                    conflictNodes={conflictNodes}
                    canBeResolved={canBeResolved}
                    loading={loading}
                    onPressMarkAsResolved={onPressMarkAsResolved}
                    {...props}
                />
            );

        // If conflictType is false then all conflicts have been resolved, no need for the banner
        case false:
            return null;

        default:
            assertNever(conflictType);
    }
}

interface PageDocumentConflictProps extends ConflictsPageBannerProps {
    canBeResolved: boolean;
    loading: boolean;
    conflictNodes: List<Block>;
    onPressMarkAsResolved: () => void;
}

const PageDocumentConflict = (props: PageDocumentConflictProps) => {
    const { canBeResolved, loading, conflictNodes, onPressMarkAsResolved } = props;

    const lastNavigatedNodeRef = React.useRef<string>();

    const scrollToNode = (key: string) => {
        const element = findDOMNode(key);
        if (!element) {
            return;
        }

        scrollToVirtualNode(element);
    };

    const scrollTo = (move: number) => {
        const previousIndex = lastNavigatedNodeRef.current
            ? conflictNodes.findIndex((block) => block.key === lastNavigatedNodeRef.current)
            : -1;
        let index = previousIndex + move;
        if (index < 0) {
            index = conflictNodes.size - 1;
        } else if (index >= conflictNodes.size) {
            index = 0;
        }

        const key = conflictNodes.get(index)?.key;
        if (key) {
            lastNavigatedNodeRef.current = key;
            scrollToNode(key);
        }
    };

    const onPressNext = () => {
        scrollTo(1);
    };

    const onPressPrev = () => {
        scrollTo(-1);
    };

    return (
        <Card
            className={clsx(
                'mb-4 flex items-center justify-between gap-4 p-4 max-xl:flex-col max-xl:items-end',
                !canBeResolved && 'flex-col items-stretch max-xl:items-stretch'
            )}
        >
            <div className="flex items-center gap-4 max-xl:items-start">
                <Badge kind="merge" size="medium" icon={Icon.Conflict} iconOnly />

                <p>
                    This page has {!canBeResolved ? conflictNodes.size : 'no'} unresolved{' '}
                    <IntlPluralize
                        count={conflictNodes.size}
                        singular="conflict"
                        plural="conflicts"
                    />{' '}
                    with the primary content.{' '}
                    {!canBeResolved
                        ? 'Resolve them by editing this document.'
                        : 'Confirm by marking this page as resolved.'}
                </p>
            </div>

            <div
                className={clsx(
                    'flex shrink-0 items-center gap-2',
                    !canBeResolved
                        ? // Padding left keeps buttons aligned with text and not overflow below badge
                          'flex-wrap justify-end pl-[44px]'
                        : 'justify-between'
                )}
            >
                {!canBeResolved ? (
                    <div className="flex items-center gap-2">
                        <Button
                            label="Prev"
                            kind="secondary"
                            trailingIcon={Icon.ChevronUp}
                            onPress={onPressPrev}
                            testID="conflictBanner.prevButton"
                        />
                        <Button
                            label="Next"
                            kind="secondary"
                            trailingIcon={Icon.ChevronDown}
                            onPress={onPressNext}
                            testID="conflictBanner.nextButton"
                        />
                    </div>
                ) : null}

                <Button
                    label="Mark as resolved"
                    kind="secondary"
                    title={
                        canBeResolved
                            ? 'Confirm resolution of all conflicts in this page'
                            : 'You need to first resolve conflicts in the document'
                    }
                    disabled={loading || !canBeResolved}
                    leadingIcon={loading ? LoadingIndicatorIcon : Icon.Check}
                    onPress={onPressMarkAsResolved}
                />
            </div>
        </Card>
    );
};

interface DeleteInBaseOrInHeadConflictProps extends ConflictsPageBannerProps {
    onPressMarkAsResolved: () => void;
    conflictType: hiveCore.RevisionConflictState;
    loading: boolean;
}

const DeleteInBaseOrInHeadConflict = (props: DeleteInBaseOrInHeadConflictProps) => {
    const { loading, onPressMarkAsResolved, page, conflictType } = props;

    const { revision } = useHiveRevision();
    const [keepModal, setKeepModal] = React.useState(false);
    const [deleteModal, setDeleteModal] = React.useState(false);

    const conflictedChildPages = hiveCore
        .getPageDescendantsInRevision(revision, page.id)
        .filter(
            (child) =>
                revision?.conflicts?.pages[child.id] ===
                    hiveCore.RevisionConflictState.DeletedInHead ||
                revision?.conflicts?.pages[child.id] ===
                    hiveCore.RevisionConflictState.DeletedInBase
        );

    const title = () => {
        const postFixTitle = 'Do you want to keep the modified page or delete it?';
        const pluralizePages = conflictedChildPages.length > 1 ? 'pages' : 'page';
        switch (conflictType) {
            case hiveCore.RevisionConflictState.DeletedInBase:
                return `This page has been deleted in the primary content, but has been modified in this change request. ${postFixTitle}`;
            case hiveCore.RevisionConflictState.DeletedInHead:
                return `This page has been deleted in this change request, but has been modified in the primary content ${postFixTitle}`;
            case hiveCore.RevisionConflictState.DeletedInBaseSubtreeModifiedInHead:
                return `This page has been deleted in the primary content but has ${conflictedChildPages.length} conflicted child ${pluralizePages}. Do you want to keep or delete it?`;
            case hiveCore.RevisionConflictState.DeletedInHeadSubtreeModifiedInBase:
                return `This page has been deleted in your change request but has ${conflictedChildPages.length} conflicted child ${pluralizePages}. Do you want to keep or delete it?`;
        }
    };

    const findUnresolvedParent = (page: hiveCore.RevisionPage) => {
        const parentPages: Array<hiveCore.RevisionPageGroup | hiveCore.RevisionPageDocument> = [];

        function iterateNodes(page: hiveCore.RevisionPage) {
            const parent = hiveCore.getPageParentInRevision(revision, page.id);

            if (parent && revision.conflicts?.pages[parent.id]) {
                parentPages.push(parent);
                iterateNodes(parent);
            }
        }

        iterateNodes(page);
        return parentPages;
    };

    const parentPages = findUnresolvedParent(page);
    const childPages = hiveCore.getPageDescendantsInRevision(revision, page.id);

    const onPressDelete = () => setDeleteModal(true);
    const onPressKeep = () => {
        if (parentPages.length || childPages.length) {
            setKeepModal(true);
            return;
        }
        onPressMarkAsResolved();
    };

    return (
        <>
            <Card className="mb-4 flex items-center justify-between gap-4 p-4 max-xl:flex-col max-xl:items-end">
                <div className="flex items-center gap-4 max-xl:items-start">
                    {conflictType ===
                        hiveCore.RevisionConflictState.DeletedInBaseSubtreeModifiedInHead ||
                    conflictType ===
                        hiveCore.RevisionConflictState.DeletedInHeadSubtreeModifiedInBase ? (
                        <Badge kind="danger" size="medium" iconOnly icon={Icon.Conflict} />
                    ) : (
                        <Badge kind="danger" size="medium" iconOnly icon={Icon.Trashcan} />
                    )}

                    <p>{title()}</p>
                </div>

                <div className="flex shrink-0 items-center gap-2">
                    <Button
                        kind="secondary"
                        leadingIcon={loading ? LoadingIndicatorIcon : Icon.Copy}
                        onPress={onPressKeep}
                        label="Keep"
                    />
                    <Button
                        kind="danger"
                        leadingIcon={loading ? LoadingIndicatorIcon : Icon.Trashcan}
                        onPress={onPressDelete}
                        label="Delete"
                    />
                </div>
            </Card>

            <ModalAnimateLifecycle>
                {keepModal ? (
                    <ConflictsKeepPageModal
                        page={page}
                        revision={revision}
                        onClose={() => setKeepModal(false)}
                        parentPages={parentPages}
                        childPages={childPages}
                    />
                ) : null}
            </ModalAnimateLifecycle>

            <DeletePageDialog
                key="modal-delete"
                isOpen={deleteModal}
                page={page}
                onConfirm={onPressMarkAsResolved}
                onClose={() => setDeleteModal(false)}
            />
        </>
    );
};
